// src/services/businessSkillsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const businessSkillsApi = createApi({
  reducerPath: 'businessSkillsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/skills`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['SkillConfig', 'SkillAssessments', 'SkillAnalytics', 'BusinessConfig', 'ConfigOptions'],
  endpoints: (builder) => ({
    // ============================================================================
    // SKILL CONFIGURATION ENDPOINTS
    // ============================================================================
    
    // Get business skill configuration
    getSkillConfiguration: builder.query({
      query: (businessId) => ({
        url: '/config',
        params: { businessId }
      }),
      providesTags: ['SkillConfig']
    }),
    
    // Update business skill configuration
    updateSkillConfiguration: builder.mutation({
      query: ({ businessId, ...configData }) => ({
        url: '/config',
        method: 'PUT',
        params: { businessId },
        body: configData
      }),
      invalidatesTags: ['SkillConfig', 'SkillAssessments', 'SkillAnalytics']
    }),

    // ============================================================================
    // SKILL ASSESSMENT MANAGEMENT ENDPOINTS
    // ============================================================================
    
    // Get pending skill assessments with filters
    getPendingSkillAssessments: builder.query({
      query: (params) => ({
        url: '/assessments',
        params: {
          businessId: params.businessId,
          status: params.status,
          trigger: params.trigger,
          department: params.department,
          role: params.role,
          limit: params.limit || 50,
          offset: params.offset || 0,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder
        }
      }),
      providesTags: ['SkillAssessments']
    }),
    
    // Get single skill assessment details
    getSkillAssessment: builder.query({
      query: ({ assessmentId, businessId }) => ({
        url: `/assessments/${assessmentId}`,
        params: { businessId }
      }),
      //@ts-ignore
      providesTags: (result, error, { assessmentId }) => [{ type: 'SkillAssessments', id: assessmentId }]
    }),
    
    // Approve skill assessment
    approveSkillAssessment: builder.mutation({
      query: ({ assessmentId, businessId, reviewerId, ...approvalData }) => ({
        url: `/assessments/${assessmentId}/approve`,
        method: 'PUT',
        params: { businessId, reviewerId },
        body: approvalData
      }),
      invalidatesTags: ['SkillAssessments', 'SkillAnalytics']
    }),
    
    // Reject skill assessment
    rejectSkillAssessment: builder.mutation({
      query: ({ assessmentId, businessId, reviewerId, ...rejectionData }) => ({
        url: `/assessments/${assessmentId}/reject`,
        method: 'PUT',
        params: { businessId, reviewerId },
        body: rejectionData
      }),
      invalidatesTags: ['SkillAssessments', 'SkillAnalytics']
    }),
    
    // Bulk process skill assessments
    bulkProcessAssessments: builder.mutation({
      query: ({ businessId, reviewerId, ...bulkData }) => ({
        url: '/assessments/bulk',
        method: 'POST',
        params: { businessId, reviewerId },
        body: bulkData
      }),
      invalidatesTags: ['SkillAssessments', 'SkillAnalytics']
    }),

    // ============================================================================
    // ANALYTICS AND REPORTING ENDPOINTS
    // ============================================================================
    
    // Get comprehensive skill analytics
    getSkillAnalytics: builder.query({
      query: (businessId) => ({
        url: '/analytics',
        params: { businessId }
      }),
      providesTags: ['SkillAnalytics']
    }),

    // ============================================================================
    // BUSINESS CONFIGURATION ENDPOINTS
    // ============================================================================
    
    // Get business configuration
    getBusinessConfiguration: builder.query({
      query: (businessId) => ({
        url: '/business-config',
        params: { businessId }
      }),
      providesTags: ['BusinessConfig']
    }),
    
    // Update business configuration
    updateBusinessConfiguration: builder.mutation({
      query: ({ businessId, ...configData }) => ({
        url: '/business-config',
        method: 'PUT',
        params: { businessId },
        body: configData
      }),
      invalidatesTags: ['BusinessConfig']
    }),
    
    // Get configuration options
    getBusinessConfigOptions: builder.query({
      query: () => ({
        url: '/business-config/options'
      }),
      providesTags: ['ConfigOptions']
    }),

    // ============================================================================
    // ADDITIONAL UTILITY ENDPOINTS
    // ============================================================================
    
    // Get skill assessment summary stats
    getAssessmentSummary: builder.query({
      query: ({ businessId, department, dateFrom, dateTo }) => ({
        url: '/assessments/summary',
        params: {
          businessId,
          department,
          dateFrom,
          dateTo
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Get department skill breakdown
    getDepartmentSkillBreakdown: builder.query({
      query: ({ businessId, department }) => ({
        url: '/analytics/department-breakdown',
        params: {
          businessId,
          department
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Get skill trends over time
    getSkillTrends: builder.query({
      query: ({ businessId, period = '30d', skillName }) => ({
        url: '/analytics/trends',
        params: {
          businessId,
          period,
          skillName
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Get employee skill matrix
    getEmployeeSkillMatrix: builder.query({
      query: ({ businessId, department, role }) => ({
        url: '/analytics/skill-matrix',
        params: {
          businessId,
          department,
          role
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Export skill data
    exportSkillData: builder.mutation({
      query: ({ businessId, format = 'excel', ...params }) => ({
        url: `/export/${format}`,
        method: 'POST',
        params: { businessId },
        body: params,
        responseHandler: (response) => response.blob()
      })
    }),
    
    // Generate skill assessment report
    generateSkillReport: builder.mutation({
      query: ({ businessId, reportType = 'comprehensive', ...reportParams }) => ({
        url: '/reports/generate',
        method: 'POST',
        params: { businessId },
        body: {
          reportType,
          ...reportParams
        },
        responseHandler: (response) => response.blob()
      })
    }),
    
    // Get skill assessment insights
    getSkillInsights: builder.query({
      query: ({ businessId, insight = 'missing-skills' }) => ({
        url: '/insights',
        params: {
          businessId,
          insight
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Trigger skill re-assessment
    triggerSkillReassessment: builder.mutation({
      query: ({ businessId, trigger, targetEmployees, reason }) => ({
        url: '/assessments/trigger-reassessment',
        method: 'POST',
        params: { businessId },
        body: {
          trigger,
          targetEmployees,
          reason
        }
      }),
      invalidatesTags: ['SkillAssessments']
    }),
    
    // Get assessment workflow status
    getAssessmentWorkflow: builder.query({
      query: ({ businessId, assessmentId }) => ({
        url: `/assessments/${assessmentId}/workflow`,
        params: { businessId }
      }),
      //@ts-ignore
      providesTags: (result, error, { assessmentId }) => [{ type: 'SkillAssessments', id: `workflow-${assessmentId}` }]
    }),
    
    // Update assessment priority
    updateAssessmentPriority: builder.mutation({
      query: ({ assessmentId, businessId, priority, reviewerId }) => ({
        url: `/assessments/${assessmentId}/priority`,
        method: 'PUT',
        params: { businessId, reviewerId },
        body: { priority }
      }),
      invalidatesTags: ['SkillAssessments']
    }),
    
    // Get skill recommendations
    getSkillRecommendations: builder.query({
      query: ({ businessId, employeeId, role, department }) => ({
        url: '/recommendations',
        params: {
          businessId,
          employeeId,
          role,
          department
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Get skill gaps analysis
    getSkillGapsAnalysis: builder.query({
      query: ({ businessId, department, requiredSkillsOnly = false }) => ({
        url: '/analytics/skill-gaps',
        params: {
          businessId,
          department,
          requiredSkillsOnly
        }
      }),
      providesTags: ['SkillAnalytics']
    }),
    
    // Preview skill report
    previewSkillReport: builder.query({
      query: ({ businessId, reportType = 'summary', department }) => ({
        url: '/reports/preview',
        params: {
          businessId,
          reportType,
          department
        }
      })
    })
  })
});

export const {
  // Configuration hooks
  useGetSkillConfigurationQuery,
  useUpdateSkillConfigurationMutation,
  
  // Assessment management hooks
  useGetPendingSkillAssessmentsQuery,
  useGetSkillAssessmentQuery,
  useApproveSkillAssessmentMutation,
  useRejectSkillAssessmentMutation,
  useBulkProcessAssessmentsMutation,
  
  // Analytics hooks
  useGetSkillAnalyticsQuery,
  useGetAssessmentSummaryQuery,
  useGetDepartmentSkillBreakdownQuery,
  useGetSkillTrendsQuery,
  useGetEmployeeSkillMatrixQuery,
  
  // Export and reporting hooks
  useExportSkillDataMutation,
  useGenerateSkillReportMutation,
  usePreviewSkillReportQuery,
  
  // Additional utility hooks
  useGetSkillInsightsQuery,
  useTriggerSkillReassessmentMutation,
  useGetAssessmentWorkflowQuery,
  useUpdateAssessmentPriorityMutation,
  useGetSkillRecommendationsQuery,
  useGetSkillGapsAnalysisQuery,
  
  // Business configuration hooks
  useGetBusinessConfigurationQuery,
  useUpdateBusinessConfigurationMutation,
  useGetBusinessConfigOptionsQuery
} = businessSkillsApi;